import { useState } from "react";
import type { Episode } from "@/lib/types";

interface EpisodePanelProps {
  episodes: Episode[];
  title: string;
}

export default function EpisodePanel({ episodes, title }: EpisodePanelProps) {
  const [active, setActive] = useState(0);
  const current = episodes[active];

  const go = (next: number) => {
    if (next < 0 || next >= episodes.length) return;
    setActive(next);
  };

  return (
    <section
      id="episodes"
      className="mt-10 md:mt-12 rounded-lg border border-background-200/70 bg-background-100/50 p-5 md:p-7 scroll-mt-24"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2.5">
          <span className="w-1 h-5 rounded-full bg-accent-500"></span>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50">
            分集剧情目录
          </h2>
        </div>
        <span className="text-[11px] text-foreground-600">
          共 {episodes.length} 集 · 点击切换查看
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[270px_minmax(0,1fr)] gap-4 lg:gap-5">
        <div className="order-2 lg:order-1 lg:max-h-[380px] lg:overflow-y-auto no-scrollbar flex gap-2 overflow-x-auto lg:flex-col lg:overflow-x-visible pb-1 lg:pb-0">
          {episodes.map((episode, index) => (
            <button
              key={episode.no}
              type="button"
              onClick={() => setActive(index)}
              className={`shrink-0 w-[200px] lg:w-full text-left px-3.5 py-3 rounded-md border transition-colors cursor-pointer ${
                index === active
                  ? "border-primary-400 bg-primary-500/15"
                  : "border-background-200/70 bg-background-50/40 hover:border-background-400"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span
                  className={`font-label text-[11px] tracking-wider ${
                    index === active ? "text-accent-500" : "text-foreground-600"
                  }`}
                >
                  EP {String(episode.no).padStart(2, "0")}
                </span>
                <span className="text-[10px] text-foreground-600">{episode.duration}</span>
              </div>
              <p
                className={`mt-1 text-xs truncate ${
                  index === active ? "text-foreground-50" : "text-foreground-300"
                }`}
              >
                {episode.title}
              </p>
            </button>
          ))}
        </div>

        <div className="order-1 lg:order-2 rounded-md border border-background-200/70 bg-background-50/50 p-4 md:p-5">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="px-2 py-0.5 rounded bg-accent-500/15 text-accent-500 text-[11px] font-label">
              EP {String(current.no).padStart(2, "0")}
            </span>
            <span className="text-[11px] text-foreground-600">{current.duration}</span>
          </div>

          <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-50">
            {current.title}
          </h3>

          <p className="mt-3 text-sm leading-7 text-foreground-400 min-h-[84px]">
            {current.synopsis}
          </p>

          <div className="mt-5 pt-4 border-t border-background-200/60 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => go(active - 1)}
              disabled={active === 0}
              className={`h-8 px-3 inline-flex items-center gap-1 rounded-md border text-xs whitespace-nowrap transition-colors ${
                active === 0
                  ? "border-background-200/50 text-foreground-800 cursor-not-allowed"
                  : "border-background-300 text-foreground-300 hover:border-accent-500 hover:text-accent-500 cursor-pointer"
              }`}
            >
              <i className="ri-arrow-left-s-line"></i>
              上一集
            </button>
            <span className="font-label text-[11px] text-foreground-700">
              {active + 1} / {episodes.length}
            </span>
            <button
              type="button"
              onClick={() => go(active + 1)}
              disabled={active === episodes.length - 1}
              className={`h-8 px-3 inline-flex items-center gap-1 rounded-md border text-xs whitespace-nowrap transition-colors ${
                active === episodes.length - 1
                  ? "border-background-200/50 text-foreground-800 cursor-not-allowed"
                  : "border-background-300 text-foreground-300 hover:border-accent-500 hover:text-accent-500 cursor-pointer"
              }`}
            >
              下一集
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        </div>
      </div>

      <p className="mt-4 text-[11px] text-foreground-600">
        《{title}》分集内容为文字剧情介绍，不包含任何视频播放功能。
      </p>
    </section>
  );
}